import {
  DEFAULT_LISTING_FILTERS,
  LISTING_SORTS,
  type ListingFilters,
  type ListingSort,
} from "./listing-filters";

export type ListingBrowserState = { filters: ListingFilters; page: number };

const PARAM = {
  query: "q",
  sort: "sort",
  conditions: "condition",
  sizes: "size",
  minPrice: "min_price",
  maxPrice: "max_price",
  maxDistance: "within",
  page: "page",
} as const;

const OWNED_PARAMS: readonly string[] = Object.values(PARAM);

function readList(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(key)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function readPositive(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function readDollarsAsCents(params: URLSearchParams, key: string): number | null {
  const dollars = readPositive(params, key);
  return dollars === null ? null : Math.round(dollars * 100);
}

function readSort(params: URLSearchParams): ListingSort {
  const raw = params.get(PARAM.sort);
  return LISTING_SORTS.find((sort) => sort === raw) ?? "default";
}

export function parseListingBrowserState(search: string): ListingBrowserState {
  const params = new URLSearchParams(search);
  return {
    filters: {
      query: params.get(PARAM.query) ?? "",
      conditions: readList(params, PARAM.conditions),
      sizes: readList(params, PARAM.sizes),
      minPriceCents: readDollarsAsCents(params, PARAM.minPrice),
      maxPriceCents: readDollarsAsCents(params, PARAM.maxPrice),
      maxDistanceMiles: readPositive(params, PARAM.maxDistance),
      sort: readSort(params),
    },
    page: Math.max(1, Math.floor(readPositive(params, PARAM.page) ?? 1)),
  };
}

function centsToDollars(cents: number): string {
  return String(cents / 100);
}

function ownedEntries({ filters, page }: ListingBrowserState): Array<[string, string]> {
  const entries: Array<[string, string | null]> = [
    [PARAM.query, filters.query.trim() === "" ? null : filters.query],
    [PARAM.sort, filters.sort === DEFAULT_LISTING_FILTERS.sort ? null : filters.sort],
    [PARAM.minPrice, filters.minPriceCents === null ? null : centsToDollars(filters.minPriceCents)],
    [PARAM.maxPrice, filters.maxPriceCents === null ? null : centsToDollars(filters.maxPriceCents)],
    [PARAM.maxDistance, filters.maxDistanceMiles === null ? null : String(filters.maxDistanceMiles)],
    [PARAM.page, page > 1 ? String(page) : null],
  ];
  return [
    ...filters.conditions.map((value): [string, string] => [PARAM.conditions, value]),
    ...filters.sizes.map((value): [string, string] => [PARAM.sizes, value]),
    ...entries.flatMap(([key, value]): Array<[string, string]> => (value === null ? [] : [[key, value]])),
  ];
}

export function writeListingBrowserSearch(
  currentSearch: string,
  state: ListingBrowserState,
): string {
  const params = new URLSearchParams(currentSearch);
  for (const key of OWNED_PARAMS) params.delete(key);
  for (const [key, value] of ownedEntries(state)) params.append(key, value);
  const search = params.toString();
  return search ? `?${search}` : "";
}
